import { Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ApiValidationFailure } from '../api/http';

/**
 * What the checks found wrong with a document, in full. Deliberately a list rather than a sentence:
 * the API reports every fault at once so a document is corrected in one round, and collapsing twenty
 * of them into a single toast line throws exactly that away.
 */
export interface ValidationFindingsTableProps {
  findings: ApiValidationFailure[];
}

/** Where the finding points: into the invoice for a rule, into the file for a schema fault. */
function locate(failure: ApiValidationFailure): string {
  const parameters = failure.parameters ?? {};

  if (typeof parameters.path === 'string' && parameters.path.length > 0) return parameters.path;

  if (typeof parameters.line === 'number' && parameters.line > 0) {
    return typeof parameters.position === 'number' && parameters.position > 0
      ? `${parameters.line}:${parameters.position}`
      : String(parameters.line);
  }

  return '—';
}

/** Anything not explicitly a warning stopped the document, so it reads as an error. */
function isWarning(failure: ApiValidationFailure): boolean {
  return failure.parameters?.severity === 'Warning';
}

export function ValidationFindingsTable({ findings }: ValidationFindingsTableProps) {
  const { t } = useTranslation();

  return (
    <Paper variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{t('files.validation.rule')}</TableCell>
            <TableCell>{t('files.validation.severity')}</TableCell>
            <TableCell>{t('files.validation.message')}</TableCell>
            <TableCell>{t('files.validation.where')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {findings.map((finding, index) => (
            <TableRow key={`${finding.code}-${index}`}>
              <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{finding.code}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={isWarning(finding) ? 'warning' : 'error'}
                  label={t(isWarning(finding) ? 'files.validation.warning' : 'files.validation.error')}
                />
              </TableCell>
              <TableCell>{finding.errorMessage ?? finding.code}</TableCell>
              {/* Paths are one long token; without a break they push the whole table sideways. */}
              <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                {locate(finding)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
